import { Link } from "react-router-dom";
import { Shield, Database, Target, Scale, Users, Globe, Clock, UserCheck, Cookie, Lock, Baby, RefreshCw, Mail, ChevronRight } from "lucide-react";

const SECTIONS = [
  {
    icon: Shield,
    title: "Responsable del Tratamiento",
    content: (
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 p-4 bg-[#15121e] rounded-xl border border-white/5">
          <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Identidad</p>
          <p className="text-white font-medium">Plazafy</p>
        </div>
        <div className="flex-1 p-4 bg-[#15121e] rounded-xl border border-white/5">
          <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Email de contacto</p>
          <p className="text-white font-medium">hola@plazafy.es</p>
        </div>
        <div className="flex-1 p-4 bg-[#15121e] rounded-xl border border-white/5">
          <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Sitio web</p>
          <p className="text-white font-medium">www.plazafy.es</p>
        </div>
      </div>
    )
  },
  {
    icon: Database,
    title: "Datos que Recopilamos",
    content: (
      <>
        <p>Recopilamos los siguientes tipos de datos personales:</p>
        {[
          { subtitle: "2.1 Datos de registro", items: ["Nombre completo", "Dirección de correo electrónico", "Número de teléfono (opcional)", "Contraseña (almacenada de forma encriptada)"] },
          { subtitle: "2.2 Datos de publicación de plazas", items: ["Dirección de la plaza de garaje", "Fotografías de la plaza", "Características y descripción", "Precios y disponibilidad"] },
          { subtitle: "2.3 Datos de reservas", items: ["Fechas de reserva", "Matrícula del vehículo (cuando se requiere)", "DNI/NIE (cuando el propietario lo solicita)", "Historial de reservas"] },
          { subtitle: "2.4 Datos de pago", items: ["Los datos de pago son procesados directamente por Stripe y PayPal", "Plazafy no almacena números de tarjeta ni datos bancarios completos", "Solo guardamos referencias de transacciones para facturación"] },
          { subtitle: "2.5 Datos técnicos", items: ["Dirección IP", "Tipo de navegador y dispositivo", "Páginas visitadas y tiempo de navegación", "Cookies (ver sección específica)"] }
        ].map((group, gi) => (
          <div key={gi} className="mt-4">
            <h4 className="text-white font-medium text-sm mb-2">{group.subtitle}</h4>
            <div className="pl-1 space-y-1.5">
              {group.items.map((item, ii) => (
                <div key={ii} className="flex items-start gap-3">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-violet-400 flex-shrink-0" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </>
    )
  },
  {
    icon: Target,
    title: "Finalidad del Tratamiento",
    content: (
      <>
        <p>Utilizamos tus datos personales para:</p>
        <ul className="list-none space-y-2 mt-3">
          {[
            { bold: "Gestión de cuentas:", text: " Crear y administrar tu cuenta de usuario." },
            { bold: "Prestación del servicio:", text: " Facilitar la publicación, búsqueda y reserva de plazas de garaje." },
            { bold: "Procesamiento de pagos:", text: " Gestionar los cobros y transferencias." },
            { bold: "Comunicaciones:", text: " Enviarte confirmaciones, notificaciones y actualizaciones del servicio." },
            { bold: "Soporte:", text: " Atender tus consultas y resolver incidencias." },
            { bold: "Mejora del servicio:", text: " Analizar el uso de la plataforma para mejorar la experiencia." },
            { bold: "Seguridad:", text: " Prevenir fraudes y actividades ilícitas." },
            { bold: "Cumplimiento legal:", text: " Cumplir con obligaciones legales y fiscales." }
          ].map((item) => (
            <li key={item} className="flex items-start gap-3">
              <span className="mt-1 w-1.5 h-1.5 rounded-full bg-violet-400 flex-shrink-0" />
              <span><strong className="text-white">{item.bold}</strong>{item.text}</span>
            </li>
          ))}
        </ul>
      </>
    )
  },
  {
    icon: Scale,
    title: "Base Legal del Tratamiento",
    content: (
      <>
        <p>El tratamiento de tus datos se basa en:</p>
        <ul className="list-none space-y-2 mt-3">
          {[
            { bold: "Ejecución de contrato:", text: " Para prestarte los servicios de la plataforma." },
            { bold: "Consentimiento:", text: " Para el envío de comunicaciones comerciales (puedes retirarlo en cualquier momento)." },
            { bold: "Interés legítimo:", text: " Para mejorar nuestros servicios y prevenir fraudes." },
            { bold: "Obligación legal:", text: " Para cumplir con requerimientos legales y fiscales." }
          ].map((item) => (
            <li key={item} className="flex items-start gap-3">
              <span className="mt-1 w-1.5 h-1.5 rounded-full bg-violet-400 flex-shrink-0" />
              <span><strong className="text-white">{item.bold}</strong>{item.text}</span>
            </li>
          ))}
        </ul>
      </>
    )
  },
  {
    icon: Users,
    title: "Destinatarios de los Datos",
    content: (
      <>
        <p>Tus datos pueden ser compartidos con:</p>
        <ul className="list-none space-y-2 mt-3">
          {[
            { bold: "Otros usuarios:", text: " Los datos necesarios para la reserva (nombre, contacto) se comparten entre propietarios e inquilinos." },
            { bold: "Proveedores de pago:", text: " Stripe y PayPal procesan los pagos según sus propias políticas de privacidad." },
            { bold: "Proveedores de servicios:", text: " Servicios de hosting, email (Resend) y análisis que actúan como encargados del tratamiento." },
            { bold: "Autoridades:", text: " Cuando sea requerido por ley o resolución judicial." }
          ].map((item) => (
            <li key={item} className="flex items-start gap-3">
              <span className="mt-1 w-1.5 h-1.5 rounded-full bg-violet-400 flex-shrink-0" />
              <span><strong className="text-white">{item.bold}</strong>{item.text}</span>
            </li>
          ))}
        </ul>
        <div className="mt-4 p-3 bg-violet-500/5 border border-violet-500/10 rounded-xl">
          <p className="text-violet-300 text-sm font-medium">No vendemos tus datos personales a terceros.</p>
        </div>
      </>
    )
  },
  {
    icon: Globe,
    title: "Transferencias Internacionales",
    content: (
      <>
        <p>
          Algunos de nuestros proveedores pueden estar ubicados fuera del Espacio Económico Europeo. 
          En estos casos, nos aseguramos de que existan garantías adecuadas:
        </p>
        <ul className="list-none space-y-2 mt-3">
          {["Decisiones de adecuación de la Comisión Europea", "Cláusulas contractuales tipo", "Certificaciones como Privacy Shield (cuando aplique)"].map((item) => (
            <li key={item} className="flex items-start gap-3">
              <span className="mt-1 w-1.5 h-1.5 rounded-full bg-violet-400 flex-shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </>
    )
  },
  {
    icon: Clock,
    title: "Plazo de Conservación",
    content: (
      <>
        <p>Conservamos tus datos durante:</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
          {[
            { label: "Datos de cuenta", time: "Mientras estes activo + 3 anos" },
            { label: "Datos de reservas", time: "5 anos (obligaciones fiscales)" },
            { label: "Datos de facturacion", time: "6 anos (obligaciones mercantiles)" },
            { label: "Comunicaciones", time: "Hasta retirar consentimiento" }
          ].map((item) => (
            <div key={item.label} className="p-3 bg-[#15121e] rounded-xl border border-white/5">
              <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">{item.label}</p>
              <p className="text-white text-sm font-medium">{item.time}</p>
            </div>
          ))}
        </div>
      </>
    )
  },
  {
    icon: UserCheck,
    title: "Tus Derechos",
    content: (
      <>
        <p>De acuerdo con el RGPD, tienes derecho a:</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4">
          {[
            { name: "Acceso", desc: "Conocer qué datos tenemos sobre ti." },
            { name: "Rectificación", desc: "Corregir datos inexactos o incompletos." },
            { name: "Supresión", desc: "Solicitar la eliminación de tus datos." },
            { name: "Oposición", desc: "Oponerte a determinados tratamientos." },
            { name: "Limitación", desc: "Limitar el tratamiento en ciertos casos." },
            { name: "Portabilidad", desc: "Recibir tus datos en formato estructurado." }
          ].map((right) => (
            <div key={right.name} className="p-3 bg-[#15121e] rounded-xl border border-white/5">
              <p className="text-violet-400 text-sm font-semibold mb-1">{right.name}</p>
              <p className="text-gray-500 text-xs leading-snug">{right.desc}</p>
            </div>
          ))}
        </div>
        <p className="mt-4 text-sm">
          Para ejercer estos derechos, contacta con nosotros en <strong className="text-violet-400">hola@plazafy.es</strong>.
        </p>
        <p className="mt-2 text-sm">
          También puedes presentar una reclamación ante la Agencia Española de Protección de Datos (AEPD): 
          <a href="https://www.aepd.es" target="_blank" rel="noopener noreferrer" className="text-violet-400 hover:text-violet-300 ml-1 underline decoration-violet-400/30">
            www.aepd.es
          </a>
        </p>
      </>
    )
  },
  {
    icon: Cookie,
    title: "Política de Cookies",
    content: (
      <>
        <p>Utilizamos cookies para:</p>
        <ul className="list-none space-y-2 mt-3">
          {[
            { bold: "Cookies técnicas:", text: " Necesarias para el funcionamiento del sitio (sesión, autenticación)." },
            { bold: "Cookies de preferencias:", text: " Guardar tus preferencias de idioma y configuración." },
            { bold: "Cookies analíticas:", text: " Analizar el uso del sitio para mejorarlo." }
          ].map((item) => (
            <li key={item} className="flex items-start gap-3">
              <span className="mt-1 w-1.5 h-1.5 rounded-full bg-violet-400 flex-shrink-0" />
              <span><strong className="text-white">{item.bold}</strong>{item.text}</span>
            </li>
          ))}
        </ul>
        <p className="mt-3 text-sm">
          Puedes configurar tu navegador para rechazar cookies, aunque esto puede afectar a la funcionalidad del sitio.
        </p>
      </>
    )
  },
  {
    icon: Lock,
    title: "Medidas de Seguridad",
    content: (
      <>
        <p>Implementamos medidas técnicas y organizativas para proteger tus datos:</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4">
          {["Encriptación HTTPS/TLS", "Contraseñas bcrypt", "Autenticación 2FA", "Acceso restringido", "Copias de seguridad", "Monitorización"].map((item) => (
            <div key={item} className="flex items-center gap-2 p-3 bg-[#15121e] rounded-xl border border-white/5">
              <Shield className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
              <span className="text-white text-xs font-medium">{item}</span>
            </div>
          ))}
        </div>
      </>
    )
  },
  {
    icon: Baby,
    title: "Menores de Edad",
    content: (
      <p>
        Nuestros servicios están dirigidos a mayores de 18 años. No recopilamos conscientemente 
        datos de menores de edad. Si detectamos que hemos recopilado datos de un menor, 
        los eliminaremos inmediatamente.
      </p>
    )
  },
  {
    icon: RefreshCw,
    title: "Modificaciones",
    content: (
      <p>
        Podemos actualizar esta Política de Privacidad periódicamente. Te notificaremos 
        de cambios significativos a través del correo electrónico o mediante un aviso en la Plataforma.
      </p>
    )
  },
  {
    icon: Mail,
    title: "Contacto",
    content: (
      <>
        <p>Para cualquier consulta sobre privacidad o protección de datos:</p>
        <div className="mt-4 flex flex-col sm:flex-row gap-4">
          <div className="flex-1 p-4 bg-[#15121e] rounded-xl border border-white/5">
            <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Email</p>
            <p className="text-white font-medium">hola@plazafy.es</p>
          </div>
          <div className="flex-1 p-4 bg-[#15121e] rounded-xl border border-white/5">
            <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Asunto</p>
            <p className="text-white font-medium">Proteccion de Datos</p>
          </div>
        </div>
      </>
    )
  }
];

export default function Privacy() {
  return (
    <div className="min-h-screen bg-[#1a1625] pt-24 pb-16" data-testid="privacy-page">
      {/* Hero header */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-violet-500/5 to-transparent pointer-events-none" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-violet-500/5 rounded-full blur-3xl pointer-events-none" />
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 py-12">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-violet-500/10 border border-violet-500/20 flex items-center justify-center">
              <Shield className="w-6 h-6 text-violet-400" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-white" data-testid="privacy-title">
                Política de Privacidad
              </h1>
              <p className="text-gray-500 text-sm mt-1">Última actualización: Diciembre 2025</p>
            </div>
          </div>
          <p className="text-gray-400 max-w-2xl">
            En Plazafy nos comprometemos a proteger tu privacidad. Esta política explica cómo 
            recopilamos, utilizamos y protegemos tus datos personales conforme al RGPD y la LOPD-GDD.
          </p>
        </div>
      </div>

      {/* Sections */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <div className="space-y-4">
          {SECTIONS.map((section, index) => {
            const Icon = section.icon;
            return (
              <div
                key={section.title}
                className="group bg-[#231f30] rounded-2xl border border-white/5 hover:border-violet-500/20 transition-all duration-300 overflow-hidden"
                data-testid={`privacy-section-${index + 1}`}
              >
                {/* Section header */}
                <div className="flex items-center gap-4 p-6 pb-0">
                  <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-violet-500/10 text-violet-400 font-bold text-sm flex-shrink-0">
                    {index + 1}
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon className="w-4 h-4 text-violet-400/60" />
                    <h2 className="text-lg font-semibold text-white">{section.title}</h2>
                  </div>
                </div>
                {/* Section content */}
                <div className="p-6 pt-4 text-gray-400 text-sm leading-relaxed space-y-2">
                  {section.content}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer navigation */}
        <div className="mt-12 p-6 bg-[#231f30] rounded-2xl border border-white/5">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-gray-500 text-sm">Consulta también nuestros términos de uso</p>
            <Link 
              to="/terms" 
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-violet-500/10 hover:bg-violet-500/20 text-violet-400 rounded-xl transition-colors text-sm font-medium"
              data-testid="privacy-to-terms-link"
            >
              Términos y Condiciones
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
